import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  ArrowLeft, Save, Trash2, Plus, Rocket, CheckSquare, Square, MessageSquare,
  Paperclip, StickyNote, ListTree, FlaskConical, Pencil, Link as LinkIcon,
  Users as UsersIcon, History as HistoryIcon
} from 'lucide-react';
import RDWhiteboard from '../components/rd/RDWhiteboard';
import RDTimeline from '../components/rd/RDTimeline';
import RDAttachmentPreview from '../components/rd/RDAttachmentPreview';
import MentionTextarea from '../components/rd/MentionTextarea';
import RDTasks from '../components/rd/RDTasks';
import { DraftHistoryDialog } from '../components/rd/DraftHistoryDialog';
import { ReworkPromotePanel } from '../components/rd/ReworkPromotePanel';
import { ApprovalBadge, ApprovalActions, APPROVER_ROLES } from '../components/rd/ApprovalControls';

const STATUS_OPTS = ['Concept', 'Prototype', 'Pilot', 'Released', 'Shelved'];
const STATUS_COLORS = {
  Concept: 'bg-slate-100 text-slate-700 border-slate-200',
  Prototype: 'bg-blue-50 text-blue-700 border-blue-200',
  Pilot: 'bg-amber-50 text-amber-700 border-amber-200',
  Released: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Shelved: 'bg-neutral-100 text-neutral-500 border-neutral-200',
};

export default function RDProjectDetailPage() {
  const { id } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingHeader, setSavingHeader] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', status: 'Concept', target_launch_date: '', owner_name: '', tags: '' });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api().get(`/rd/projects/${id}`);
      setData(r.data);
      const p = r.data.project || {};
      setForm({
        name: p.name || '',
        description: p.description || '',
        status: p.status || 'Concept',
        target_launch_date: p.target_launch_date || '',
        owner_name: p.owner_name || '',
        tags: (p.tags || []).join(', '),
      });
    } catch (e) {
      toast.error(e?.response?.status === 404 ? 'Project not found' : 'Failed to load project');
      navigate('/rd');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const saveHeader = async () => {
    setSavingHeader(true);
    try {
      await api().put(`/rd/projects/${id}`, {
        name: form.name,
        description: form.description,
        status: form.status,
        target_launch_date: form.target_launch_date || null,
        owner_name: form.owner_name || null,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      });
      toast.success('Project saved');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingHeader(false);
    }
  };

  if (loading || !data) return <div className="p-8 text-slate-500">Loading project…</div>;
  const project = data.project || {};

  return (
    <div className="p-6 space-y-5" data-testid="rd-project-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/rd')} data-testid="rd-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> R&D Projects
          </Button>
          <FlaskConical className="w-5 h-5 text-fuchsia-600" />
          <h1 className="text-xl font-semibold text-slate-800">{project.name}</h1>
          <Badge className={`${STATUS_COLORS[project.status] || ''} border text-[11px]`}>{project.status}</Badge>
        </div>
      </div>

      <Tabs defaultValue={searchParams.get('tab') || 'overview'}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="rd-tab-overview"><Pencil className="w-3.5 h-3.5 mr-1" />Overview</TabsTrigger>
          <TabsTrigger value="boms" data-testid="rd-tab-boms"><ListTree className="w-3.5 h-3.5 mr-1" />Draft BOMs</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="rd-tab-tasks"><UsersIcon className="w-3.5 h-3.5 mr-1" />Dependencies</TabsTrigger>
          <TabsTrigger value="whiteboard" data-testid="rd-tab-whiteboard"><StickyNote className="w-3.5 h-3.5 mr-1" />Whiteboard</TabsTrigger>
          <TabsTrigger value="milestones" data-testid="rd-tab-milestones"><CheckSquare className="w-3.5 h-3.5 mr-1" />Milestones</TabsTrigger>
          <TabsTrigger value="comments" data-testid="rd-tab-comments"><MessageSquare className="w-3.5 h-3.5 mr-1" />Comments</TabsTrigger>
          <TabsTrigger value="files" data-testid="rd-tab-files"><Paperclip className="w-3.5 h-3.5 mr-1" />Files</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="space-y-4">
            {/* iter129 — timeline strip */}
            <RDTimeline project={project} />
            <Card className="p-5 space-y-4" data-testid="rd-overview-card">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-600">Name</label>
                <Input data-testid="rd-overview-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Status</label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="rd-overview-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Owner</label>
                <Input data-testid="rd-overview-owner" value={form.owner_name} onChange={e => setForm({ ...form, owner_name: e.target.value })} placeholder="Who's driving this?" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Target launch date</label>
                <Input type="date" data-testid="rd-overview-target-date" value={form.target_launch_date} onChange={e => setForm({ ...form, target_launch_date: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-slate-600">Description</label>
                <Textarea data-testid="rd-overview-description" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-slate-600">Tags (comma-separated)</label>
                <Input data-testid="rd-overview-tags" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="e.g. firmware, battery, cost-reduction" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={saveHeader} disabled={savingHeader} data-testid="rd-save-header-btn">
                <Save className="w-4 h-4 mr-1" /> {savingHeader ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </Card>
          </div>
        </TabsContent>

        <TabsContent value="boms">
          <RDDraftBoms project={project} draftBoms={data.draft_boms || []} onChange={load} />
        </TabsContent>

        <TabsContent value="tasks">
          <RDTasks projectId={id} tasks={data.tasks || []} onChange={load} />
        </TabsContent>

        <TabsContent value="whiteboard">
          <RDWhiteboard projectId={id} initialState={data.whiteboard || { nodes: [], edges: [] }} />
        </TabsContent>

        <TabsContent value="milestones">
          <RDMilestones projectId={id} milestones={data.milestones || []} onChange={load} />
        </TabsContent>

        <TabsContent value="comments">
          <RDComments projectId={id} comments={data.comments || []} currentUser={user} onChange={load} />
        </TabsContent>

        <TabsContent value="files">
          <RDFiles projectId={id} attachments={data.attachments || []} onChange={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// Draft BOMs panel
// ============================================================
function RDDraftBoms({ project, draftBoms, onChange }) {
  const { api, user } = useAuth();
  const isApprover = APPROVER_ROLES.includes(user?.role);
  const [submitNote, setSubmitNote] = useState('');
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null); // bom object or null for create
  const emptyBom = { name: '', revision: '0.1', notes: '', lines: [] };
  const [bomForm, setBomForm] = useState(emptyBom);
  const [promoteFor, setPromoteFor] = useState(null);
  const [promoteForm, setPromoteForm] = useState({ product_type_id: '', bom_variant_code: '', description: '' });
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [productTypes, setProductTypes] = useState([]);
  const [historyFor, setHistoryFor] = useState(null);
  const isRework = promoteFor?.kind === 'rework';

  useEffect(() => {
    api().get('/product-types').then(r => setProductTypes(r.data || [])).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setBomForm(emptyBom); setShow(true); };
  const openEdit = (b) => { setEditing(b); setBomForm({ name: b.name, revision: b.revision, notes: b.notes || '', lines: b.lines || [] }); setShow(true); };

  const addLine = () => setBomForm(f => ({ ...f, lines: [...f.lines, { component_sku: '', quantity_per_unit: 1, uom: 'EA', scrap_factor_percent: 0, notes: '' }] }));
  const updateLine = (i, k, v) => setBomForm(f => { const lines = [...f.lines]; lines[i] = { ...lines[i], [k]: v }; return { ...f, lines }; });
  const removeLine = (i) => setBomForm(f => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!bomForm.name.trim()) { toast.error('BOM name required'); return; }
    try {
      const payload = { ...bomForm, lines: bomForm.lines.filter(l => (l.component_sku || '').trim()) };
      if (editing) await api().put(`/rd/projects/${project.id}/draft-boms/${editing.id}`, payload);
      else await api().post(`/rd/projects/${project.id}/draft-boms`, payload);
      toast.success('Draft BOM saved');
      setShow(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete draft BOM "${b.name}"?`)) return;
    try { await api().delete(`/rd/projects/${project.id}/draft-boms/${b.id}`); toast.success('Deleted'); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const openPromote = async (b) => {
    setPromoteFor(b);
    setPromoteForm({ product_type_id: '', bom_variant_code: b.revision || '1.0', description: b.kind === 'rework' ? '' : `Promoted from R&D: ${b.name}` });
    setPreview(null);
    setPreviewLoading(true);
    try {
      const r = await api().get(`/rd/projects/${project.id}/draft-boms/${b.id}/preview`);
      setPreview(r.data);
    } catch (e) {
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const promote = async () => {
    if (!isRework && !promoteForm.product_type_id) { toast.error('Pick a product type'); return; }
    if (!isRework && !promoteForm.bom_variant_code.trim()) { toast.error('Variant code required'); return; }
    try {
      const body = isRework ? { description: promoteForm.description } : promoteForm;
      if (!isApprover) {
        await api().post(`/rd/projects/${project.id}/draft-boms/${promoteFor.id}/submit-approval`, { ...body, note: submitNote });
        toast.success('Submitted for approval. Admins and Production Supervisors have been notified');
        setPromoteFor(null);
        setSubmitNote('');
        onChange();
        return;
      }
      const r = await api().post(`/rd/projects/${project.id}/draft-boms/${promoteFor.id}/promote`, body);
      toast.success(isRework ? `Promoted as production rev ${r.data?.bom?.bom_revision}` : 'Promoted to production BOM');
      setPromoteFor(null);
      onChange();
      if (r.data?.bom?.id) {
        // Give the user a path to the new BOM
        setTimeout(() => { if (window.confirm('Open the new production BOM?')) window.location.href = `/boms/view/${r.data.bom.id}`; }, 400);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Promotion failed');
    }
  };

  return (
    <Card className="p-5" data-testid="rd-boms-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-slate-800">Draft BOMs</h2>
        <Button size="sm" onClick={openCreate} data-testid="rd-add-draft-bom-btn"><Plus className="w-4 h-4 mr-1" /> New draft BOM</Button>
      </div>
      {draftBoms.length === 0 ? (
        <p className="text-sm text-slate-500">No draft BOMs yet. Sketch one here; promote to production when it's ready.</p>
      ) : (
        <div className="space-y-2">
          {draftBoms.map(b => (
            <div key={b.id} className="border border-slate-200 rounded p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`rd-draft-bom-${b.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-800">{b.name}</span>
                  <Badge variant="outline" className="text-[10px]">rev {b.revision}</Badge>
                  {b.status === 'Promoted' && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-[10px]">Promoted{b.promoted_revision ? ` as rev ${b.promoted_revision}` : ''}</Badge>}
                  <ApprovalBadge draft={b} />
                  {b.kind === 'rework' && (
                    <Badge className="bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 border text-[10px]" data-testid={`rd-draft-bom-rework-badge-${b.id}`}>
                      Rework of {b.bom_variant_code} rev {b.source_bom_revision}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{(b.lines || []).length} lines{b.notes ? ` · ${b.notes.slice(0, 60)}${b.notes.length > 60 ? '…' : ''}` : ''}</div>
                {b.approval?.status === 'rejected' && b.approval.comment && (
                  <div className="text-xs text-rose-700 mt-1" data-testid={`rd-reject-comment-${b.id}`}>Rejected by {b.approval.by_name}: “{b.approval.comment}”</div>
                )}
                {b.approval?.status === 'pending' && (
                  <div className="text-xs text-amber-700 mt-1">Submitted by {b.approval.submitted_by_name}{b.approval.comment ? `: “${b.approval.comment}”` : ''}</div>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setHistoryFor(b)} data-testid={`rd-draft-bom-history-${b.id}`}><HistoryIcon className="w-3.5 h-3.5 mr-1" />History</Button>
                {b.status !== 'Promoted' && b.approval?.status === 'pending' && <ApprovalActions draft={b} onChange={onChange} />}
                {b.status !== 'Promoted' && b.approval?.status !== 'pending' && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => openEdit(b)} data-testid={`rd-edit-draft-bom-${b.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" onClick={() => openPromote(b)} data-testid={`rd-promote-draft-bom-${b.id}`}><Rocket className="w-3.5 h-3.5 mr-1" />{isApprover ? 'Promote' : 'Submit for approval'}</Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(b)} data-testid={`rd-delete-draft-bom-${b.id}`}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></Button>
                  </>
                )}
                {b.status === 'Promoted' && b.promoted_to_bom_id && (
                  <Button size="sm" variant="outline" onClick={() => window.location.href = `/boms/view/${b.promoted_to_bom_id}`}>
                    <LinkIcon className="w-3.5 h-3.5 mr-1" /> Open production BOM
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="max-w-3xl" data-testid="rd-draft-bom-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} draft BOM</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600">Name</label>
                <Input data-testid="rd-draft-bom-name" value={bomForm.name} onChange={e => setBomForm({ ...bomForm, name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Revision</label>
                <Input data-testid="rd-draft-bom-revision" value={bomForm.revision} disabled={editing?.kind === 'rework'} onChange={e => setBomForm({ ...bomForm, revision: e.target.value })} />
                {editing?.kind === 'rework' && <p className="text-[10px] text-slate-400 mt-0.5">Auto: bumps to -rd.{(editing.rd_iteration || 1) + 1} when you save changes</p>}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Notes</label>
              <Textarea rows={2} value={bomForm.notes} onChange={e => setBomForm({ ...bomForm, notes: e.target.value })} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-slate-600">Lines</label>
                <Button size="sm" variant="outline" onClick={addLine} data-testid="rd-add-bom-line-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add line</Button>
              </div>
              {bomForm.lines.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No lines yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {bomForm.lines.map((l, i) => (
                    <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`rd-bom-line-${i}`}>
                      <Input className="col-span-4" placeholder="SKU" value={l.component_sku} onChange={e => updateLine(i, 'component_sku', e.target.value)} />
                      <Input className="col-span-2" type="number" step="0.01" value={l.quantity_per_unit} onChange={e => updateLine(i, 'quantity_per_unit', parseFloat(e.target.value) || 0)} />
                      <Input className="col-span-1" placeholder="UoM" value={l.uom} onChange={e => updateLine(i, 'uom', e.target.value)} />
                      <Input className="col-span-4" placeholder="Notes" value={l.notes || ''} onChange={e => updateLine(i, 'notes', e.target.value)} />
                      <Button variant="ghost" size="sm" className="col-span-1" onClick={() => removeLine(i)}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancel</Button>
            <Button onClick={save} data-testid="rd-save-draft-bom-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!promoteFor} onOpenChange={(v) => !v && setPromoteFor(null)}>
        <DialogContent className="max-w-3xl" data-testid="rd-promote-dialog">
          <DialogHeader><DialogTitle>{isRework ? 'Promote rework back to production' : 'Promote to production BOM'}</DialogTitle></DialogHeader>
          {isRework
            ? <ReworkPromotePanel rework={preview?.rework} />
            : <p className="text-xs text-slate-500">This clones <b>{promoteFor?.name}</b> into the production <code>boms</code> collection as a new <b>Draft</b> BOM.</p>}

          {/* iter128 — Promote preview rollup */}
          <div className="border border-slate-200 rounded p-3 bg-slate-50" data-testid="rd-promote-preview">
            {previewLoading && <p className="text-xs text-slate-500">Calculating cost + stock impact…</p>}
            {!previewLoading && preview && (
              <>
                <div className="flex items-center gap-3 text-xs mb-2 flex-wrap">
                  <span><b>Lines:</b> {preview.line_count}</span>
                  <span className="text-emerald-700"><b>✓ In stock:</b> {preview.ok_count}</span>
                  <span className="text-amber-700"><b>⚠ Short:</b> {preview.short_count}</span>
                  <span className="text-rose-700"><b>✖ Missing:</b> {preview.missing_count}</span>
                  <span className="ml-auto font-mono"><b>Cost per unit:</b> ${preview.total_cost_per_unit?.toFixed(2) || '0.00'}</span>
                </div>
                {preview.lines.length > 0 && (
                  <div className="max-h-48 overflow-y-auto border border-slate-200 rounded bg-white">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 text-slate-600">
                        <tr>
                          <th className="text-left px-2 py-1">SKU</th>
                          <th className="text-left px-2 py-1">Need</th>
                          <th className="text-left px-2 py-1">On hand</th>
                          <th className="text-right px-2 py-1">Unit $</th>
                          <th className="text-right px-2 py-1">Ext $</th>
                          <th className="text-left px-2 py-1">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.lines.map((l, i) => (
                          <tr key={i} className="border-t border-slate-100" data-testid={`rd-preview-row-${i}`}>
                            <td className="px-2 py-1 font-mono">{l.sku || <span className="text-rose-500 italic">no sku</span>}</td>
                            <td className="px-2 py-1">{l.effective_qty} {l.uom}</td>
                            <td className="px-2 py-1">{l.current_quantity ?? '—'}</td>
                            <td className="px-2 py-1 text-right">{l.unit_cost != null ? `$${l.unit_cost.toFixed(2)}` : '—'}</td>
                            <td className="px-2 py-1 text-right font-mono">{l.extended_cost != null ? `$${l.extended_cost.toFixed(2)}` : '—'}</td>
                            <td className="px-2 py-1">
                              {l.status === 'ok' && <span className="text-emerald-700 text-[10px]">✓ ok</span>}
                              {l.status === 'short' && <span className="text-amber-700 text-[10px]" title={l.message}>⚠ short by {l.short_by}</span>}
                              {l.status === 'missing' && <span className="text-rose-700 text-[10px]" title={l.message}>✖ {l.message || 'missing'}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {preview.missing_count > 0 && (
                  <p className="text-xs text-rose-700 mt-2" data-testid="rd-preview-blocker-msg">
                    {preview.missing_count} SKU{preview.missing_count > 1 ? 's are' : ' is'} missing from Items master. Add them first, then re-run preview.
                  </p>
                )}
              </>
            )}
          </div>

          <div className="space-y-3">
            {!isRework && <>
            <div>
              <label className="text-xs font-medium text-slate-600">Target product type</label>
              <Select value={promoteForm.product_type_id} onValueChange={v => setPromoteForm({ ...promoteForm, product_type_id: v })}>
                <SelectTrigger data-testid="rd-promote-product-type"><SelectValue placeholder="Pick a product type" /></SelectTrigger>
                <SelectContent>
                  {productTypes.map(pt => <SelectItem key={pt.id} value={pt.id}>{pt.name || pt.type_name || pt.product_type_name || pt.id}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">BOM variant code</label>
              <Input data-testid="rd-promote-variant" value={promoteForm.bom_variant_code} onChange={e => setPromoteForm({ ...promoteForm, bom_variant_code: e.target.value })} />
            </div>
            </>}
            <div>
              <label className="text-xs font-medium text-slate-600">Description</label>
              <Textarea rows={2} value={promoteForm.description} placeholder={isRework ? 'Leave blank to keep the production description' : ''} onChange={e => setPromoteForm({ ...promoteForm, description: e.target.value })} data-testid="rd-promote-description" />
            </div>
            {!isApprover && (
              <div>
                <label className="text-xs font-medium text-slate-600">Note for the approver (optional)</label>
                <Textarea rows={2} value={submitNote} onChange={e => setSubmitNote(e.target.value)} data-testid="rd-submit-approval-note" />
                <p className="text-[11px] text-slate-500 mt-1">An Admin or Production Supervisor must approve. Approval promotes it automatically.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoteFor(null)}>Cancel</Button>
            <Button
              onClick={promote}
              disabled={preview?.promote_blockers > 0}
              data-testid="rd-promote-submit-btn"
              title={preview?.promote_blockers > 0 ? 'Resolve missing SKUs / open work orders first' : ''}
            >
              <Rocket className="w-4 h-4 mr-1" />{isApprover ? 'Approve & promote' : 'Submit for approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DraftHistoryDialog draft={historyFor} onClose={() => setHistoryFor(null)} />
    </Card>
  );
}

// ============================================================
// Milestones panel
// ============================================================
function RDMilestones({ projectId, milestones, onChange }) {
  const { api } = useAuth();
  const [form, setForm] = useState({ title: '', due_date: '', description: '' });

  const add = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    try {
      await api().post(`/rd/projects/${projectId}/milestones`, {
        title: form.title,
        due_date: form.due_date || null,
        description: form.description || null,
      });
      setForm({ title: '', due_date: '', description: '' });
      onChange();
    } catch (e) { toast.error('Create failed'); }
  };
  const toggle = async (m) => {
    try { await api().put(`/rd/projects/${projectId}/milestones/${m.id}`, { done: !m.done }); onChange(); }
    catch { toast.error('Update failed'); }
  };
  const remove = async (m) => {
    try { await api().delete(`/rd/projects/${projectId}/milestones/${m.id}`); onChange(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <Card className="p-5" data-testid="rd-milestones-card">
      <h2 className="font-semibold text-slate-800 mb-3">Milestones</h2>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Input className="flex-1 min-w-[180px]" data-testid="rd-milestone-title" placeholder="New milestone title…" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
        <Input type="date" className="w-44" data-testid="rd-milestone-date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
        <Button onClick={add} data-testid="rd-add-milestone-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
      </div>
      {milestones.length === 0 ? (
        <p className="text-sm text-slate-500">No milestones yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {milestones.map(m => (
            <li key={m.id} className="flex items-center gap-3 border border-slate-200 rounded px-3 py-2" data-testid={`rd-milestone-${m.id}`}>
              <button onClick={() => toggle(m)} className="text-slate-500 hover:text-emerald-600" data-testid={`rd-milestone-toggle-${m.id}`}>
                {m.done ? <CheckSquare className="w-4 h-4 text-emerald-600" /> : <Square className="w-4 h-4" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`text-sm ${m.done ? 'line-through text-slate-400' : 'text-slate-800'}`}>{m.title}</div>
                {m.description && <div className="text-xs text-slate-500">{m.description}</div>}
              </div>
              {m.due_date && <span className="text-xs text-slate-500">{m.due_date}</span>}
              <button onClick={() => remove(m)} className="text-rose-500 hover:text-rose-700" data-testid={`rd-milestone-remove-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ============================================================
// Comments panel
// ============================================================
function RDComments({ projectId, comments, currentUser, onChange }) {
  const { api } = useAuth();
  const [body, setBody] = useState('');
  const send = async () => {
    if (!body.trim()) return;
    try { await api().post(`/rd/projects/${projectId}/comments`, { body }); setBody(''); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
  };
  const remove = async (c) => {
    try { await api().delete(`/rd/projects/${projectId}/comments/${c.id}`); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  // iter132 — highlight @mentions in rendered body
  const renderBody = (txt) => {
    const parts = (txt || '').split(/(@[A-Za-z0-9._-]+)/g);
    return parts.map((p, i) =>
      p.startsWith('@')
        ? <span key={i} className="bg-fuchsia-100 text-fuchsia-800 px-1 rounded text-[12px] font-medium">{p}</span>
        : <React.Fragment key={i}>{p}</React.Fragment>
    );
  };

  return (
    <Card className="p-5" data-testid="rd-comments-card">
      <h2 className="font-semibold text-slate-800 mb-3">Comments</h2>
      <div className="space-y-2 mb-4 max-h-[50vh] overflow-y-auto pr-2">
        {comments.length === 0 ? <p className="text-sm text-slate-500">Nothing to discuss yet. Start the thread — type @ to mention a teammate.</p> :
          comments.map(c => (
            <div key={c.id} className="border border-slate-200 rounded px-3 py-2 text-sm flex items-start gap-2" data-testid={`rd-comment-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-slate-500"><b className="text-slate-700">{c.user_name}</b> · {(c.ts || '').slice(0, 16).replace('T', ' ')}</div>
                <div className="whitespace-pre-wrap text-slate-800 mt-0.5">{renderBody(c.body)}</div>
              </div>
              {(c.user_id === currentUser?.id || currentUser?.role === 'Admin') && (
                <button onClick={() => remove(c)} className="text-rose-500 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
          ))}
      </div>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <MentionTextarea
            value={body}
            onChange={setBody}
            placeholder="Leave a note. Type @ to mention a teammate…"
            rows={2}
            dataTestId="rd-comment-input"
          />
        </div>
        <Button onClick={send} data-testid="rd-send-comment-btn"><MessageSquare className="w-4 h-4 mr-1" />Post</Button>
      </div>
    </Card>
  );
}

// ============================================================
// Files panel (URL-based attachments v1)
// ============================================================
function RDFiles({ projectId, attachments, onChange }) {
  const { api, token } = useAuth();
  const [form, setForm] = useState({ filename: '', url: '', description: '' });
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const fileInputRef = React.useRef(null);
  const replaceInputRefs = React.useRef({});

  const addLink = async () => {
    if (!form.filename.trim() || !form.url.trim()) { toast.error('Filename and URL required'); return; }
    try { await api().post(`/rd/projects/${projectId}/attachments`, form); setForm({ filename: '', url: '', description: '' }); onChange(); }
    catch (e) { toast.error('Add failed'); }
  };

  const uploadFile = async (fileObj, descOverride) => {
    if (!fileObj) return;
    if (fileObj.size > 25 * 1024 * 1024) { toast.error('File too large (max 25 MB)'); return; }
    const fd = new FormData();
    fd.append('file', fileObj);
    if (descOverride != null) fd.append('description', descOverride);
    else if (form.description) fd.append('description', form.description);
    setUploading(true);
    try {
      await api().post(`/rd/projects/${projectId}/attachments/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('File uploaded');
      setForm({ filename: '', url: '', description: '' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const replaceFile = async (attachmentId, fileObj) => {
    if (!fileObj) return;
    if (fileObj.size > 25 * 1024 * 1024) { toast.error('File too large (max 25 MB)'); return; }
    const fd = new FormData();
    fd.append('file', fileObj);
    try {
      await api().post(`/rd/projects/${projectId}/attachments/${attachmentId}/replace`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('New version uploaded');
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Replace failed');
    } finally {
      if (replaceInputRefs.current[attachmentId]) replaceInputRefs.current[attachmentId].value = '';
    }
  };

  const remove = async (a) => {
    try { await api().delete(`/rd/projects/${projectId}/attachments/${a.id}`); onChange(); }
    catch { toast.error('Delete failed'); }
  };

  // Build an auth-prefetched URL for the download endpoint
  const downloadUrl = (a, inline = true) => {
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    return `${base}/api/rd/projects/${projectId}/attachments/${a.id}/download?inline=${inline ? 'true' : 'false'}`;
  };

  const openAttachment = async (a) => {
    if (a.url) { window.open(a.url, '_blank', 'noopener'); return; }
    if (!a.storage_path) return;
    // iter132 — open the inline preview modal instead of a new tab
    setPreviewing(a);
  };

  return (
    <Card className="p-5" data-testid="rd-files-card">
      <h2 className="font-semibold text-slate-800 mb-3">Files &amp; Links</h2>

      {/* Upload file */}
      <div className="border border-slate-200 rounded p-3 mb-3 bg-slate-50">
        <div className="text-xs font-medium text-slate-600 mb-2">Upload a document (PDF, image, CAD, spreadsheet… up to 25 MB)</div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="file"
            ref={fileInputRef}
            onChange={(e) => uploadFile(e.target.files?.[0])}
            disabled={uploading}
            className="text-xs"
            data-testid="rd-file-upload-input"
          />
          {uploading && <span className="text-xs text-slate-500">Uploading…</span>}
        </div>
      </div>

      {/* Or paste a link */}
      <div className="border border-slate-200 rounded p-3 mb-4">
        <div className="text-xs font-medium text-slate-600 mb-2">…or paste an external link</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Input data-testid="rd-file-name" placeholder="Filename / label" value={form.filename} onChange={e => setForm({ ...form, filename: e.target.value })} />
          <Input data-testid="rd-file-url" placeholder="https://drive.google.com/…" value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} />
          <div className="flex gap-2">
            <Input data-testid="rd-file-description" placeholder="Short description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            <Button onClick={addLink} data-testid="rd-add-file-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
          </div>
        </div>
      </div>

      {attachments.length === 0 ? (
        <p className="text-sm text-slate-500">No files or links yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {attachments.map(a => (
            <li key={a.id} className="flex items-center gap-3 border border-slate-200 rounded px-3 py-2" data-testid={`rd-attachment-${a.id}`}>
              <Paperclip className="w-4 h-4 text-slate-400" />
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => openAttachment(a)}
                  className="text-sm text-blue-600 hover:underline text-left break-all"
                  data-testid={`rd-attachment-open-${a.id}`}
                >
                  {a.filename}
                </button>
                {a.version > 1 && <span className="ml-2 text-[10px] bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 px-1.5 py-0.5 rounded">v{a.version}</span>}
                {a.description && <div className="text-xs text-slate-500">{a.description}</div>}
                <div className="text-[10px] text-slate-400">
                  {a.storage_path ? 'uploaded file' : 'external link'}
                  {a.size_bytes ? ` · ${(a.size_bytes / 1024).toFixed(1)} KB` : ''}
                  {' · '}by {a.uploaded_by_name} · {(a.uploaded_at || '').slice(0, 10)}
                </div>
              </div>
              {a.storage_path && (
                <>
                  <a
                    href={downloadUrl(a, false)}
                    onClick={async (e) => {
                      e.preventDefault();
                      const res = await fetch(downloadUrl(a, false), { headers: { Authorization: `Bearer ${token}` } });
                      const blob = await res.blob();
                      const blobUrl = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = blobUrl; link.download = a.filename; link.click();
                      setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
                    }}
                    className="text-xs text-slate-500 hover:text-slate-800 underline"
                    data-testid={`rd-attachment-download-${a.id}`}
                  >Download</a>
                  <label
                    className="text-xs text-slate-500 hover:text-fuchsia-700 cursor-pointer underline"
                    data-testid={`rd-attachment-replace-label-${a.id}`}
                  >
                    Replace
                    <input
                      type="file"
                      ref={el => { replaceInputRefs.current[a.id] = el; }}
                      onChange={(e) => replaceFile(a.id, e.target.files?.[0])}
                      className="hidden"
                      data-testid={`rd-attachment-replace-input-${a.id}`}
                    />
                  </label>
                </>
              )}
              <button onClick={() => remove(a)} className="text-rose-500 hover:text-rose-700" data-testid={`rd-attachment-remove-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}

      {/* iter132 — inline preview modal */}
      <RDAttachmentPreview
        projectId={projectId}
        attachment={previewing}
        open={!!previewing}
        onClose={() => setPreviewing(null)}
      />
    </Card>
  );
}
